import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationHistoryContext = createContext();

export const NotificationHistoryProvider = ({ children }) => {
  const [notifications, setNotifications] = useState(() => {
    // Charger depuis localStorage au démarrage
    try {
      const saved = localStorage.getItem('notification_history');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Nettoyer les notifications chargées pour éviter les problèmes avec les SVG
        return parsed.map(notif => {
          // S'assurer que pageIcon est bien une chaîne valide
          if (notif.pageIcon && typeof notif.pageIcon === 'string') {
            const trimmedIcon = notif.pageIcon.trim();
            // Vérifier que c'est bien un SVG valide (commence par <svg et contient </svg>)
            if (!trimmedIcon.startsWith('<svg') || !trimmedIcon.includes('</svg>')) {
              // Si ce n'est pas un SVG valide, le supprimer
              return { ...notif, pageIcon: null };
            }
            // Vérifier que le SVG n'est pas corrompu (doit contenir au moins xmlns, viewBox ou path)
            if (!trimmedIcon.includes('xmlns') && !trimmedIcon.includes('viewBox') && !trimmedIcon.includes('path')) {
              // SVG probablement corrompu, le supprimer
              return { ...notif, pageIcon: null };
            }
          }
          // Nettoyer le message pour éviter l'affichage de code SVG brut
          if (notif.message && typeof notif.message === 'string') {
            const trimmedMessage = notif.message.trim();
            // Si le message contient du code SVG, c'est probablement une erreur
            if (trimmedMessage.startsWith('<svg') && trimmedMessage.includes('</svg>')) {
              // Remplacer par un message d'erreur générique
              return { ...notif, message: 'Notification (icône invalide)' };
            }
          }
          return notif;
        });
      }
    } catch (e) {
      console.error('Erreur lors du chargement de l\'historique des notifications:', e);
    }
    return [];
  });

  // Sauvegarder dans localStorage à chaque modification
  const saveToStorage = useCallback((newNotifications) => {
    try {
      // Nettoyer les notifications avant sauvegarde pour éviter les problèmes de sérialisation
      const cleanedNotifications = newNotifications.map(notif => {
        // S'assurer que pageIcon est bien une chaîne valide
        if (notif.pageIcon && typeof notif.pageIcon === 'string') {
          const trimmedIcon = notif.pageIcon.trim();
          // Vérifier que c'est bien un SVG valide (commence par <svg et contient </svg>)
          if (!trimmedIcon.startsWith('<svg') || !trimmedIcon.includes('</svg>')) {
            // Si ce n'est pas un SVG valide, le supprimer
            return { ...notif, pageIcon: null };
          }
          // Vérifier que le SVG n'est pas corrompu (doit contenir au moins xmlns, viewBox ou path)
          if (!trimmedIcon.includes('xmlns') && !trimmedIcon.includes('viewBox') && !trimmedIcon.includes('path')) {
            // SVG probablement corrompu, le supprimer
            return { ...notif, pageIcon: null };
          }
        }
        // Nettoyer le message pour éviter l'affichage de code SVG brut
        if (notif.message && typeof notif.message === 'string') {
          const trimmedMessage = notif.message.trim();
          // Si le message contient du code SVG, c'est probablement une erreur
          if (trimmedMessage.startsWith('<svg') && trimmedMessage.includes('</svg>')) {
            // Remplacer par un message d'erreur générique
            return { ...notif, message: 'Notification (icône invalide)' };
          }
        }
        return notif;
      });
      localStorage.setItem('notification_history', JSON.stringify(cleanedNotifications));
    } catch (e) {
      console.error('Erreur lors de la sauvegarde de l\'historique des notifications:', e);
    }
  }, []);

  const addNotification = useCallback((notification) => {
    setNotifications(prev => {
      // Vérifier si une notification identique existe déjà
      const now = Date.now();
      const isDuplicate = prev.some(notif => {
        const timeDiff = now - new Date(notif.timestamp).getTime();
        // Pour "Paramètre sauvegardé" : fusionner plusieurs sauvegardes rapprochées
        const isParamSave = (notification.type || 'info') === 'success' &&
          notification.title === 'Paramètre sauvegardé' &&
          notification.pageName === 'Paramètres';
        if (isParamSave && timeDiff < 3000) {
          const notifIsParamSave = notif.type === 'success' && notif.title === 'Paramètre sauvegardé' && notif.pageName === 'Paramètres';
          return notifIsParamSave;
        }
        return (
          notif.message === notification.message &&
          notif.type === (notification.type || 'info') &&
          notif.title === (notification.title || null) &&
          notif.pageName === (notification.pageName || null) &&
          timeDiff < 5000
        );
      });
      
      // Si c'est un doublon récent, ne pas l'ajouter
      if (isDuplicate) {
        return prev;
      }
      
      const newNotification = {
        id: Date.now() + Math.random(), // ID unique
        timestamp: new Date().toISOString(),
        message: notification.message,
        type: notification.type || 'info',
        title: notification.title || null,
        pageName: notification.pageName || null,
        pageIcon: notification.pageIcon || null,
        level: notification.level ?? 3, // 1=haute, 2=moyenne, 3=basse (défaut pour rétrocompat)
      };

      // Ajouter la nouvelle notification en début de liste
      const updated = [newNotification, ...prev];
      // Limiter à 100 notifications maximum pour éviter de surcharger le localStorage
      const limited = updated.slice(0, 100);
      saveToStorage(limited);
      return limited;
    });
  }, [saveToStorage]);

  const clearHistory = useCallback(() => {
    setNotifications([]);
    saveToStorage([]);
  }, [saveToStorage]);

  const getRecentNotifications = useCallback((limit = 10) => {
    return notifications.slice(0, limit);
  }, [notifications]);

  const getUnreadCount = useCallback(() => {
    // Pour l'instant, on considère toutes les notifications comme lues
    // On pourrait ajouter un champ "read" plus tard si nécessaire
    return 0;
  }, []);

  return (
    <NotificationHistoryContext.Provider
      value={{
        notifications,
        addNotification,
        clearHistory,
        getRecentNotifications,
        getUnreadCount,
      }}
    >
      {children}
    </NotificationHistoryContext.Provider>
  );
};

export const useNotificationHistory = () => {
  const context = useContext(NotificationHistoryContext);
  if (!context) {
    throw new Error('useNotificationHistory must be used within NotificationHistoryProvider');
  }
  return context;
};

